import { useState, type CSSProperties, type ReactNode } from "react";
import { Eye, EyeOff, LogOut } from "lucide-react";
import { T } from "../lib/theme";
import { supabase } from "../lib/supabase";

/** Email + password.
 *
 *  Google OAuth and magic links were tried first (see docs/HANDOFF.md §2.3) but
 *  both hit real setup friction: Supabase's shared email sender caps out at a
 *  couple of sends an hour without hooking up your own SMTP provider, and
 *  turning on Google sign-in means a trip through Google Cloud Console before
 *  it works at all. For a handful of people, a password is less friction than
 *  either.
 *
 *  There is no self-service sign-up here, matching how the allowlist already
 *  works: whoever manages the ward list creates each person's login directly in
 *  Supabase → Authentication → Users (email, password, "Auto Confirm User"
 *  checked so no confirmation email is sent), and adds their email to
 *  sacrament_allowed_emails. Two separate gates — an account has to exist
 *  before someone can sign in at all, and their email has to be on the
 *  allowlist before RLS will show them anything.
 *
 *  Forgotten passwords are also an admin action for the same reason: Supabase's
 *  password-reset flow emails a link, which runs into the same rate limit.
 */
export function SignIn() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const signIn = async () => {
    const address = email.trim();
    if (!address || !password) return;
    setBusy(true);
    setError(null);
    const { error } = await supabase.auth.signInWithPassword({ email: address, password });
    setBusy(false);
    if (error) setError(error.message);
    // On success, useSession's onAuthStateChange listener picks up the new
    // session and App re-renders past this screen.
  };

  return (
    <Shell>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          void signIn();
        }}
      >
        <label className="eyebrow" style={{ color: "rgba(255,255,255,.55)", display: "block", marginBottom: 6 }}>
          Email
        </label>
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@example.com"
          autoComplete="email"
          autoFocus
          style={fieldStyle}
        />

        <label
          className="eyebrow"
          style={{ color: "rgba(255,255,255,.55)", display: "block", margin: "14px 0 6px" }}
        >
          Password
        </label>
        <div style={{ position: "relative" }}>
          <input
            type={showPassword ? "text" : "password"}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="current-password"
            style={{ ...fieldStyle, paddingRight: 44 }}
          />
          <button
            type="button"
            onClick={() => setShowPassword((v) => !v)}
            aria-label={showPassword ? "Hide password" : "Show password"}
            style={{
              position: "absolute",
              right: 4,
              top: 0,
              bottom: 0,
              padding: "0 10px",
              color: "rgba(255,255,255,.55)",
              display: "flex",
              alignItems: "center",
            }}
          >
            {showPassword ? <EyeOff size={17} /> : <Eye size={17} />}
          </button>
        </div>

        <button
          type="submit"
          disabled={!email.trim() || !password || busy}
          style={primaryButton(Boolean(email.trim()) && Boolean(password))}
        >
          {busy ? "Signing in…" : "Sign in"}
        </button>
      </form>

      <p style={{ fontSize: 12.5, color: "rgba(255,255,255,.45)", lineHeight: 1.6, margin: "16px 0 0" }}>
        Don't have a login, or forgot your password? Ask whoever manages the ward list — accounts are set up
        directly, not self-service.
      </p>

      {error && (
        <div
          style={{
            marginTop: 16,
            fontSize: 13,
            color: "#F3C7C2",
            background: "rgba(140,47,38,.35)",
            padding: "10px 12px",
            borderRadius: 8,
            lineHeight: 1.5,
          }}
        >
          {error}
        </div>
      )}
    </Shell>
  );
}

/** Signed in with a real account, but not on the allowlist. Without this it just
 *  looks like the app is broken — every screen would be empty. */
export function NoAccess({ email, onSignOut }: { email: string; onSignOut: () => void }) {
  return (
    <Shell title="You're not on the list">
      <p style={{ fontSize: 14.5, color: "rgba(255,255,255,.8)", lineHeight: 1.6, margin: "0 0 4px" }}>
        You're signed in as <strong style={{ color: "#fff" }}>{email}</strong>, but that address hasn't been given
        access yet.
      </p>
      <p style={{ fontSize: 13.5, color: "rgba(255,255,255,.55)", lineHeight: 1.6, margin: "0 0 22px" }}>
        Ask whoever set this up to add you, then sign in again.
      </p>
      <button onClick={onSignOut} style={secondaryButton(true)}>
        <LogOut size={16} /> Sign out
      </button>
    </Shell>
  );
}

/* ---------------------------------------------------------------- */

function Shell({ title, children }: { title?: string; children: ReactNode }) {
  return (
    <div
      style={{
        minHeight: "100vh",
        background: T.navy,
        color: "#fff",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "32px 22px",
      }}
    >
      <div style={{ width: "100%", maxWidth: 380 }}>
        <div className="eyebrow" style={{ color: "rgba(255,255,255,.45)" }}>
          Sacrament meeting
        </div>
        <h1 className="serif" style={{ fontSize: 30, fontWeight: 500, margin: "6px 0 8px", letterSpacing: "-.015em" }}>
          {title ?? "Sacrament Speakers"}
        </h1>
        {!title && (
          <p style={{ fontSize: 14, color: "rgba(255,255,255,.55)", lineHeight: 1.6, margin: "0 0 26px" }}>
            Sign in to see who's due to speak.
          </p>
        )}
        {title && <div style={{ height: 12 }} />}

        {children}

        <div
          style={{
            marginTop: 34,
            paddingTop: 18,
            borderTop: "1px solid rgba(255,255,255,.10)",
            fontSize: 11,
            color: "rgba(255,255,255,.38)",
            lineHeight: 1.55,
          }}
        >
          This app is created by the Hunter 9th Ward. It is not an official app of The Church of Jesus Christ of
          Latter-day Saints.
        </div>
      </div>
    </div>
  );
}

const fieldStyle: CSSProperties = {
  width: "100%",
  padding: "12px 14px",
  borderRadius: 10,
  border: "1px solid rgba(255,255,255,.16)",
  background: "rgba(255,255,255,.08)",
  color: "#fff",
  fontSize: 16,
};

const primaryButton = (enabled: boolean): CSSProperties => ({
  width: "100%",
  marginTop: 20,
  padding: 14,
  borderRadius: 11,
  background: enabled ? "#fff" : "rgba(255,255,255,.16)",
  color: enabled ? T.navy : "rgba(255,255,255,.45)",
  fontSize: 15,
  fontWeight: 600,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: 10,
  cursor: enabled ? "pointer" : "default",
});

const secondaryButton = (enabled: boolean): CSSProperties => ({
  width: "100%",
  padding: 13,
  borderRadius: 11,
  background: "rgba(255,255,255,.10)",
  color: enabled ? "#fff" : "rgba(255,255,255,.4)",
  fontSize: 14.5,
  fontWeight: 600,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: 8,
  cursor: enabled ? "pointer" : "default",
});
